import { AlertCircle, Check, Loader2 } from "lucide-react";
import { Trans, useTranslation } from "react-i18next";

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { CodeInput } from "@/components/shared/CodeInput";
import { CoverageAreaPicker } from "@/components/shared/coverage-area-picker";
import { FileUpload } from "@/components/shared/FileUpload";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PasswordInput } from "@/components/ui/password-input";
import { PasswordStrengthIndicator } from "@/components/shared/PasswordStrengthIndicator";
import type { IdentityNames, RequirementKey } from "@/pages/auth/professional-invite-steps";
import type { ProfessionalInvitePhone } from "@/pages/auth/use-professional-invite-phone";

/**
 * The parts of an acceptance wizard that do not depend on how a profession is
 * verified (spec 42ec3c57, slice 3).
 *
 * WHY SHARED COPY RATHER THAN A NAMESPACE PROP. These sections carried
 * identical Spanish and English strings under two key prefixes —
 * `auth.acceptNotaryInvitation.password` and
 * `auth.acceptAccountantInvitation.password` both read "Contraseña". Threading
 * a prefix through would preserve a distinction that does not exist and would
 * make a THIRD prefix necessary the day a composed flow has no single
 * profession to name. They move to `auth.acceptProfessionalInvitation`, which
 * is what a wizard built from a profession's configuration can actually use.
 *
 * EXTRACTED ONE AT A TIME, each verified before the next. The previous section
 * in this programme was restructured in one pass, spliced on a closing tag
 * that was not the block boundary, and had to be reverted.
 */

export interface PasswordSectionProps {
  formId: string;
  onSubmit: (event: React.FormEvent) => void;
  password: string;
  confirmPassword: string;
  onPasswordChange: (value: string) => void;
  onConfirmPasswordChange: (value: string) => void;
  disabled?: boolean;
}

/**
 * Choosing the account credential.
 *
 * ITS OWN STEP in the notary flow, and the reasoning is worth keeping: a
 * password and a tax identity are different questions asked of different parts
 * of a person's life, and bundling them made the billing step the tallest in
 * the wizard.
 */
export function PasswordSection({
  formId,
  onSubmit,
  password,
  confirmPassword,
  onPasswordChange,
  onConfirmPasswordChange,
  disabled = false,
}: PasswordSectionProps): JSX.Element {
  const { t } = useTranslation();

  return (
    <form id={formId} onSubmit={onSubmit} className="mt-6 flex flex-col gap-4">
      <div className="grid gap-2">
        <Label htmlFor="password">{t("auth.acceptProfessionalInvitation.password")}</Label>
        <PasswordInput
          id="password"
          autoComplete="new-password"
          value={password}
          onChange={(e) => onPasswordChange(e.target.value)}
          minLength={8}
          required
          disabled={disabled}
        />
        <PasswordStrengthIndicator
          password={password}
          showRules
          rulesLabels={{
            minLength: t("auth.register.passwordStrength.minLength"),
            uppercase: t("auth.register.passwordStrength.uppercase"),
            number: t("auth.register.passwordStrength.number"),
            special: t("auth.register.passwordStrength.special"),
          }}
          strengthLabels={{
            weak: t("auth.register.passwordStrength.weak"),
            fair: t("auth.register.passwordStrength.fair"),
            good: t("auth.register.passwordStrength.good"),
            strong: t("auth.register.passwordStrength.strong"),
          }}
        />
      </div>
      <div className="grid gap-2">
        <Label htmlFor="confirmPassword">
          {t("auth.acceptProfessionalInvitation.confirmPassword")}
        </Label>
        <PasswordInput
          id="confirmPassword"
          autoComplete="new-password"
          value={confirmPassword}
          onChange={(e) => onConfirmPasswordChange(e.target.value)}
          minLength={8}
          required
          disabled={disabled}
        />
      </div>
    </form>
  );
}

export interface ProfileSectionProps {
  formId: string;
  onSubmit: (event: React.FormEvent) => void;
  /** As the verification established them — not the applicant's to type. */
  firstName: string;
  lastName: string;
  onProfileImageChange: (file: File | null) => void;
}

/**
 * The name verification established, and a photo.
 *
 * THE NAME FIELDS ARE READ-ONLY, and that is the point of showing them: the
 * applicant sees who the register says they are before they go further, rather
 * than typing a name that will be overwritten.
 *
 * ONE PAIR OF FIELDS, NOT FOUR. The accountant flow asks for four name parts
 * on its identity step, because a document lookup returns them separately and
 * can fail in a way that needs manual correction. That is a property of THAT
 * verification, not of this concern, and it belongs in that strategy's
 * identity section rather than here.
 */
export function ProfileSection({
  formId,
  onSubmit,
  firstName,
  lastName,
  onProfileImageChange,
}: ProfileSectionProps): JSX.Element {
  const { t } = useTranslation();

  return (
    <form id={formId} onSubmit={onSubmit} className="mt-6 flex flex-col gap-4">
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="grid gap-2">
          <Label htmlFor="firstName">{t("auth.acceptProfessionalInvitation.firstName")}</Label>
          <Input id="firstName" type="text" value={firstName} disabled readOnly className="bg-muted" />
        </div>
        <div className="grid gap-2">
          <Label htmlFor="lastName">{t("auth.acceptProfessionalInvitation.lastName")}</Label>
          <Input id="lastName" type="text" value={lastName} disabled readOnly className="bg-muted" />
        </div>
      </div>
      <div className="grid gap-2 pt-1">
        <Label>{t("auth.acceptProfessionalInvitation.profileImage")}</Label>
        <p className="text-xs text-muted-foreground">
          {t("auth.acceptProfessionalInvitation.profileImageHelp")}
        </p>
        <FileUpload currentImageUrl={null} onFileChange={onProfileImageChange} showLabel={false} />
      </div>
    </form>
  );
}

export interface CoverageSectionProps {
  formId: string;
  onSubmit: (event: React.FormEvent) => void;
  available: readonly string[] | undefined;
  selected: string[];
  onChange: (next: string[]) => void;
  idPrefix: string;
}

/**
 * Where the professional works.
 *
 * NEVER REQUIRED, which is why its step's gate is the constant true. An empty
 * selection is a legitimate answer that shows every request unranked; blocking
 * on it pushes people into ticking departments they do not serve, which is
 * worse than nothing because it looks like data.
 *
 * ASKED HERE BECAUSE THIS IS THE ONLY MOMENT THE PROFESSIONAL IS RELIABLY
 * PRESENT. It used to live on a profile card reachable after signup, where 0
 * of 8 professionals ever filled it in — so the ranking it feeds had never
 * once changed an ordering.
 */
export function CoverageSection({
  formId,
  onSubmit,
  available,
  selected,
  onChange,
  idPrefix,
}: CoverageSectionProps): JSX.Element {
  const { t } = useTranslation();

  return (
    <form id={formId} onSubmit={onSubmit} className="mt-6 flex flex-col gap-4">
      {available?.length ? (
        <>
          <CoverageAreaPicker
            available={[...available]}
            selected={selected}
            onChange={onChange}
            idPrefix={idPrefix}
          />
          <p className="text-xs text-muted-foreground">
            {selected.length === 0
              ? t("auth.professionalCoverage.noneSelected")
              : t("auth.professionalCoverage.help")}
          </p>
        </>
      ) : (
        // The vocabulary is served WITH the invitation; without it there is
        // nothing to offer, and the step says so rather than rendering an
        // empty box.
        <p className="text-sm text-muted-foreground">
          {t("auth.professionalCoverage.noneSelected")}
        </p>
      )}
    </form>
  );
}

export interface BillingSectionProps {
  formId: string;
  onSubmit: (event: React.FormEvent) => void;
  rut: string;
  razonSocial: string;
  issuesCfe: boolean | null;
  onRutChange: (value: string) => void;
  onRazonSocialChange: (value: string) => void;
  onIssuesCfeChange: (value: boolean) => void;
  disabled?: boolean;
}

/**
 * The fiscal declaration: RUT, razon social, and whether they issue CFEs.
 *
 * BOTH ANSWERS TO THE CFE QUESTION ARE A COMPLETE DECLARATION. "No" routes to
 * help rather than rejecting, because a pequena empresa issues e-Boletas,
 * which ARE CFEs — treating "charges no IVA" as "cannot invoice" would exclude
 * a legal category of professional.
 *
 * THE RUT IS DIGITS ONLY as typed, so the 12-digit rule that the DTO and the
 * column both enforce cannot be failed by a dot or a hyphen someone pasted.
 */
export function BillingSection({
  formId,
  onSubmit,
  rut,
  razonSocial,
  issuesCfe,
  onRutChange,
  onRazonSocialChange,
  onIssuesCfeChange,
  disabled = false,
}: BillingSectionProps): JSX.Element {
  const { t } = useTranslation();

  return (
    <form id={formId} onSubmit={onSubmit} className="mt-6 flex flex-col gap-5">
      <div className="grid gap-2">
        <Label htmlFor="fiscal-rut">{t("auth.professionalFiscal.rutLabel")}</Label>
        <Input
          id="fiscal-rut"
          inputMode="numeric"
          autoComplete="off"
          maxLength={12}
          placeholder={t("auth.professionalFiscal.rutPlaceholder")}
          value={rut}
          onChange={(e) => onRutChange(e.target.value.replace(/\D/g, ""))}
          disabled={disabled}
        />
        <p className="text-xs text-muted-foreground">{t("auth.professionalFiscal.rutHelp")}</p>
      </div>

      <div className="grid gap-2">
        <Label htmlFor="fiscal-razon-social">
          {t("auth.professionalFiscal.razonSocialLabel")}
        </Label>
        <Input
          id="fiscal-razon-social"
          autoComplete="organization"
          maxLength={255}
          placeholder={t("auth.professionalFiscal.razonSocialPlaceholder")}
          value={razonSocial}
          onChange={(e) => onRazonSocialChange(e.target.value)}
          disabled={disabled}
        />
        <p className="text-xs text-muted-foreground">
          {t("auth.professionalFiscal.razonSocialHelp")}
        </p>
      </div>

      <div className="grid gap-2">
        <Label>{t("auth.professionalFiscal.cfeLabel")}</Label>
        <div className="flex flex-col gap-2 sm:flex-row">
          <Button
            type="button"
            variant={issuesCfe === true ? "default" : "outline"}
            className="flex-1 transition-colors duration-150"
            onClick={() => onIssuesCfeChange(true)}
            disabled={disabled}
          >
            {t("auth.professionalFiscal.cfeYes")}
          </Button>
          <Button
            type="button"
            variant={issuesCfe === false ? "default" : "outline"}
            className="flex-1 transition-colors duration-150"
            onClick={() => onIssuesCfeChange(false)}
            disabled={disabled}
          >
            {t("auth.professionalFiscal.cfeNo")}
          </Button>
        </div>
      </div>

      {issuesCfe === false ? (
        // Shown, not blocking. Fiscalo is named because the platform already
        // integrates it for its own invoicing, NOT as the only acceptable
        // provider — making one vendor a condition of earning here would be a
        // tying problem.
        <div className="rounded-lg border border-border bg-background p-4 text-sm">
          <p className="font-medium text-foreground">
            {t("auth.professionalFiscal.noCfeTitle")}
          </p>
          <p className="mt-1 text-muted-foreground">
            {t("auth.professionalFiscal.noCfeBody")}
          </p>
          {/*
            THE WAY OUT, which only the accountant page offered. Every
            professional is now the supplier of record for their own work
            (spec 300908fc), so a notary answering "todavía no" has exactly
            the same problem and was being told so with nothing to act on.
            There is no signup API, so this is an outbound link and nothing
            more.
          */}
          <a
            href="https://fiscalo.uy"
            target="_blank"
            rel="noreferrer noopener"
            className="mt-2 inline-block text-primary underline-offset-4 hover:underline"
          >
            {t("auth.professionalFiscal.noCfeLink")}
          </a>
        </div>
      ) : null}
    </form>
  );
}

export interface PhoneSectionProps {
  formId: string;
  onSubmit: (event: React.FormEvent) => void;
  phone: ProfessionalInvitePhone;
}

/**
 * The phone gate, in its THREE states, on its own screen.
 *
 * ASK FOR THE NUMBER, THEN ASK FOR THE CODE — never both at once. An earlier
 * version showed them together, so the screen opened with an empty code box
 * beside a dead Verify button: two disabled affordances and no obvious first
 * move. Every reference flow (Wise, Upwork, Fresha, Turo) does it in two.
 *
 * THE NUMBER IS REPEATED BACK in the code state, with a way to change it. A
 * code arriving at a mistyped number is the commonest failure here, and the
 * only way out is to see what was typed.
 *
 * The step's action lives in the FOOTER, so one primary button changes job
 * (enviar, verificar, continuar) rather than three competing on the card.
 */
export function PhoneSection({ formId, onSubmit, phone }: PhoneSectionProps): JSX.Element {
  const { t } = useTranslation();

  return (
    <form id={formId} onSubmit={onSubmit} className="mt-6 flex flex-col gap-4">
      {!phone.codeSent && !phone.verified ? (
        <div className="grid gap-2">
          <Label htmlFor="professional-invite-phone">
            {t("auth.acceptProfessionalInvitation.phone")}
          </Label>
          <Input
            id="professional-invite-phone"
            type="tel"
            autoComplete="tel"
            inputMode="tel"
            placeholder={t("auth.acceptProfessionalInvitation.phonePlaceholder")}
            value={phone.phone}
            onChange={(e) => phone.onPhoneChange(e.target.value)}
            autoFocus
            className="transition-colors duration-150"
          />
          {/* Help BELOW the field, where it reads as a note rather than as a
              second instruction between label and input. */}
          <p className="text-xs text-muted-foreground">
            {t("auth.acceptProfessionalInvitation.phoneHelp")}
          </p>
        </div>
      ) : phone.verified ? (
        <div className="flex flex-wrap items-center gap-3">
          <Badge
            variant="secondary"
            className="w-fit gap-1 border border-emerald-200 bg-emerald-50 pl-2 pr-2.5 text-emerald-900 shadow-none dark:border-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-100"
          >
            <Check
              className="h-3.5 w-3.5 shrink-0 text-emerald-600 dark:text-emerald-400"
              aria-hidden
            />
            {t("auth.acceptProfessionalInvitation.phoneVerified")}
          </Badge>
          <span className="text-sm text-muted-foreground">{phone.phone}</span>
        </div>
      ) : (
        <>
          <p className="text-sm text-muted-foreground">
            <Trans
              i18nKey="auth.acceptProfessionalInvitation.phoneCodeSentTo"
              values={{ phone: phone.phone.trim() }}
              components={{ strong: <span className="font-medium text-foreground" /> }}
            />{" "}
            <button
              type="button"
              className="font-medium text-foreground underline underline-offset-2 transition-colors hover:text-primary"
              onClick={phone.reset}
            >
              {t("auth.acceptProfessionalInvitation.phoneChangeNumber")}
            </button>
          </p>
          <div className="grid gap-2">
            <Label htmlFor="professional-invite-phone-otp">
              {t("auth.acceptProfessionalInvitation.phoneCodeLabel")}
            </Label>
            {/*
              THE SAME SEGMENTED INPUT THE 2FA SCREENS USE. A single
              letter-spaced field had to carry a "Código del SMS" placeholder,
              which rendered as spaced-out ghost text and said nothing about
              how many digits were wanted. Six boxes say it without any copy.
            */}
            <CodeInput
              id="professional-invite-phone-otp"
              value={phone.otp}
              onChange={phone.setOtp}
              autoFocus
              disabled={phone.verifying}
              className="justify-start"
            />
          </div>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
            <button
              type="button"
              className="font-medium underline-offset-2 transition-colors hover:text-foreground hover:underline disabled:cursor-not-allowed disabled:opacity-60"
              disabled={phone.sending || phone.resendSecondsLeft > 0}
              onClick={() => void phone.sendCode("sms")}
            >
              {phone.resendSecondsLeft > 0
                ? t("auth.acceptProfessionalInvitation.phoneResendIn", {
                    seconds: phone.resendSecondsLeft,
                  })
                : t("auth.acceptProfessionalInvitation.phoneResend")}
            </button>
            <button
              type="button"
              className="font-medium underline-offset-2 transition-colors hover:text-foreground hover:underline disabled:cursor-not-allowed disabled:opacity-60"
              disabled={phone.sending}
              onClick={() => void phone.sendCode("call")}
            >
              {t("auth.acceptProfessionalInvitation.phoneCallInstead")}
            </button>
          </div>
        </>
      )}
    </form>
  );
}

/**
 * The applicant's name AND the state of the lookup that filled it.
 *
 * THE LOOKUP STATE IS PART OF THE FIELD, not decoration around it. These
 * inputs are read-only while the register is answering and while it has
 * answered — the point of asking the register is that the name matches the
 * document — and become writable only when the lookup could not answer. One
 * object carries both so a caller cannot wire the values and forget the state,
 * which would leave four permanently disabled empty boxes on the one path
 * where the applicant is the only available source.
 */
export interface IdentityNamesControl {
  value: IdentityNames;
  onChange: (part: keyof IdentityNames, value: string) => void;
  /** The register is answering; the block is masked rather than empty. */
  loading: boolean;
  /** The register could not answer, so the applicant types the names. */
  manualEntry: boolean;
}

/**
 * The name parts, as data, so the four inputs cannot drift apart.
 *
 * They were four hand-written blocks differing only in label, id and
 * autocomplete hint, which is how three of them come to share a rule the
 * fourth quietly does not. The second surname's `off` is carried over
 * deliberately rather than corrected: the autocomplete vocabulary has no
 * token for it, `family-name` already belongs to the first surname, and
 * guessing one here would be a behaviour change inside a refactor.
 */
const NAME_PARTS: readonly {
  part: keyof IdentityNames;
  id: string;
  labelKey: string;
  autoComplete: string;
}[] = [
  {
    part: "firstName",
    id: "professional-invite-first-name",
    labelKey: "auth.acceptProfessionalInvitation.names.first",
    autoComplete: "given-name",
  },
  {
    part: "secondFirstName",
    id: "professional-invite-second-first-name",
    labelKey: "auth.acceptProfessionalInvitation.names.secondFirst",
    autoComplete: "additional-name",
  },
  {
    part: "firstLastName",
    id: "professional-invite-first-last-name",
    labelKey: "auth.acceptProfessionalInvitation.names.firstLast",
    autoComplete: "family-name",
  },
  {
    part: "secondLastName",
    id: "professional-invite-second-last-name",
    labelKey: "auth.acceptProfessionalInvitation.names.secondLast",
    autoComplete: "off",
  },
];

export interface IdentitySectionProps {
  formId: string;
  onSubmit: (event: React.FormEvent) => void;
  /** What this profession's verifications ask for, from the composer. */
  requirements: readonly RequirementKey[];
  cedula: string;
  onCedulaChange: (value: string) => void;
  /*
    EVERYTHING BELOW IS OPTIONAL AND CHECKED AGAINST `requirements`.

    A strategy that verifies no credential has no credential to pass, and a
    page that passed an empty string with a no-op handler would be wiring
    nothing to nowhere — dead props are how a field comes to render against
    state that is never read. So each requirement brings its own inputs, and
    asking for one without them is refused rather than rendered blank.
  */
  credential?: string;
  onCredentialChange?: (value: string) => void;
  /** Required by `identity.names`. */
  names?: IdentityNamesControl;
  /** Required by `identity.credential`: the profession's own words for it. */
  professionCopy?: { professionNoun: string; profession: string; credentialLabel: string; credential: string };
}

/**
 * Who the applicant is, asked according to what their profession's
 * verifications actually need (spec 42ec3c57).
 *
 * THIS IS THE SECTION COMPOSITION TURNS ON. Every strategy wants the cédula;
 * only a register-backed one wants a credential. Rendering from the
 * requirement keys rather than from a profession is what lets two strategies
 * share this step and ask for the union once — the whole reason a strategy
 * contributes REQUIREMENTS and not steps.
 *
 * IT REFUSES A REQUIREMENT IT CANNOT RENDER rather than omitting the field.
 * A silently missing input is FM-1: the applicant is never asked for
 * something verifyAll will refuse them for, and finds out part-way through
 * their own signup. That refusal now covers a second shape — a requirement
 * this section knows how to render but was given nothing to render it WITH:
 * `identity.names` without a `names` control would otherwise paint four
 * permanently empty boxes, which fails FM-1 just as quietly as omitting them.
 *
 * `identity.names` IS THE ACCOUNTANT'S FOUR-PART BREAKDOWN, and it exists
 * because a document lookup returns the parts separately and can fail in a
 * way that needs manual correction — so the fields are read-only when the
 * register answered and writable when it did not. A register-backed strategy
 * (poder_judicial) does not ask for them at all: it returns a name as a fact
 * about the credential, which the profile step then shows read-only. Both
 * strategies that DO ask for them (document_lookup, manual_review) carry the
 * same failure mode, which is why the control models the lookup state rather
 * than the accountant page's particular booleans.
 */
export function IdentitySection({
  formId,
  onSubmit,
  requirements,
  cedula,
  credential,
  onCedulaChange,
  onCredentialChange,
  names,
  professionCopy,
}: IdentitySectionProps): JSX.Element {
  const { t } = useTranslation();

  const renderable: RequirementKey[] = [
    "identity.cedula",
    "identity.credential",
    "identity.names",
  ];
  const unrenderable = requirements.filter((key) => !renderable.includes(key));
  if (unrenderable.length > 0) {
    throw new Error(
      `IdentitySection cannot ask for ${unrenderable.join(", ")}. ` +
        `Rendering the step without those fields would refuse the applicant for ` +
        `something they were never asked (FM-1).`,
    );
  }

  /*
    A REQUIREMENT ASKED FOR WITHOUT ITS INPUTS IS THE SAME FM-1 IN A DIFFERENT
    SHAPE — the field is on screen and cannot be filled, or is absent
    altogether. Both end with verifyAll refusing an applicant for something
    they were never really asked, so both are refused here instead.
  */
  const wantsNames = requirements.includes("identity.names");
  const wantsCredential = requirements.includes("identity.credential");
  const missing = [
    wantsNames && !names ? "identity.names needs a `names` control" : null,
    wantsCredential && onCredentialChange == null
      ? "identity.credential needs `credential` and `onCredentialChange`"
      : null,
    wantsCredential && !professionCopy
      ? "identity.credential needs `professionCopy` to name the credential"
      : null,
  ].filter((entry): entry is string => entry != null);
  if (missing.length > 0) {
    throw new Error(
      `IdentitySection cannot render what it was asked for: ${missing.join("; ")}.`,
    );
  }

  return (
    <form id={formId} onSubmit={onSubmit} className="mt-6 flex flex-col gap-4">
      {requirements.includes("identity.cedula") ? (
        <div className="grid gap-2">
          <Label htmlFor="cedula">{t("auth.acceptProfessionalInvitation.cedula")}</Label>
          <Input
            id="cedula"
            type="text"
            inputMode="numeric"
            autoComplete="off"
            placeholder="12345678"
            value={cedula}
            // Digits only as typed, so the 7-9 rule cannot be failed by a
            // pasted dot or hyphen.
            onChange={(e) => onCedulaChange(e.target.value.replace(/\D/g, ""))}
            maxLength={9}
            required
          />
          <p className="text-xs text-muted-foreground">
            {t("auth.acceptProfessionalInvitation.cedulaHelp")}
          </p>
        </div>
      ) : null}

      {wantsNames && names ? (
        <div className="relative rounded-lg border border-border">
          {/*
            ORDERED WHO-YOU-ARE THEN WHAT-YOU-HOLD, not by the order the
            requirements arrive in. A composed profession's requirement set is
            built by union and its order follows which strategy was configured
            first, which is not a fact about the applicant; name before
            credential reads the same way for every combination.
          */}
          {names.loading ? (
            <div
              className="absolute inset-0 z-10 flex items-center justify-center rounded-lg bg-background/70 motion-reduce:bg-background/90"
              aria-busy="true"
              aria-live="polite"
            >
              <Loader2
                className="h-6 w-6 shrink-0 animate-spin text-muted-foreground motion-reduce:animate-none"
                aria-hidden
              />
              <span className="sr-only">
                {t("auth.acceptProfessionalInvitation.names.loading")}
              </span>
            </div>
          ) : null}
          {names.manualEntry ? (
            <div className="px-4 pt-4">
              {/*
                THE ALERT IS WHY THESE FIELDS ARE WRITABLE AT ALL. Without it
                the applicant meets four boxes that were read-only a moment
                ago and has no reason to believe typing in them is wanted.
              */}
              <Alert>
                <AlertCircle className="h-4 w-4" aria-hidden />
                <AlertTitle>
                  {t("auth.acceptProfessionalInvitation.names.manualEntryTitle")}
                </AlertTitle>
                <AlertDescription>
                  {t("auth.acceptProfessionalInvitation.names.manualEntryBody")}
                </AlertDescription>
              </Alert>
            </div>
          ) : null}
          <div className="grid gap-4 p-4 sm:grid-cols-2">
            {NAME_PARTS.map(({ part, id, labelKey, autoComplete }) => (
              <div className="grid gap-2" key={part}>
                <Label htmlFor={id}>{t(labelKey)}</Label>
                <Input
                  id={id}
                  type="text"
                  autoComplete={autoComplete}
                  readOnly={!names.manualEntry}
                  disabled={!names.manualEntry}
                  /*
                    OUT OF THE TAB ORDER WHILE READ-ONLY. A disabled input is
                    already unreachable by keyboard in practice; being explicit
                    keeps four dead stops out of the sequence between the
                    cédula and the footer button.
                  */
                  tabIndex={names.manualEntry ? undefined : -1}
                  value={names.value[part]}
                  onChange={(e) => names.onChange(part, e.target.value)}
                  className={names.manualEntry ? undefined : "bg-muted"}
                />
              </div>
            ))}
          </div>
        </div>
      ) : null}

      {wantsNames ? (
        <p className="text-xs text-muted-foreground">
          {t("auth.acceptProfessionalInvitation.names.help")}
        </p>
      ) : null}

      {wantsCredential && professionCopy && onCredentialChange ? (
        <div className="grid gap-2">
          {/*
            NAMED BY THE PROFESSION, not by the notary's word for it. A
            register may issue something that is not called a matrícula, which
            is why the portal's copy resolver carries credentialLabel as its
            own form.
          */}
          <Label htmlFor="credential">
            {t("auth.acceptProfessionalInvitation.credential", professionCopy)}
          </Label>
          <Input
            id="credential"
            type="text"
            inputMode="numeric"
            autoComplete="off"
            placeholder="4600"
            value={credential ?? ""}
            onChange={(e) => onCredentialChange(e.target.value.replace(/\D/g, ""))}
            required
          />
          <p className="text-xs text-muted-foreground">
            {t("auth.acceptProfessionalInvitation.credentialHelp", professionCopy)}
          </p>
        </div>
      ) : null}
    </form>
  );
}
